import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { twMerge } from 'tailwind-merge'
import { Country, countries, getCountry } from '../../constants/countries'

type Props = {
  // ISO 3166-1 alpha-2 code, e.g. 'IN'
  value: string
  onChange: (code: Country['code']) => void
  className?: string
  ariaLabel?: string
}

// Height of the search box above the list, and the roomiest the list should get.
const SEARCH_HEIGHT = 60
const MAX_LIST_HEIGHT = 256
const MIN_LIST_HEIGHT = 140

// Bottom edge of whatever would clip the dropdown — the enclosing modal or
// bottom sheet if there is one, otherwise the viewport.
const clippingBottom = (el: HTMLElement) => {
  let node: HTMLElement | null = el.parentElement
  while (node) {
    const { overflowY } = getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden') {
      return Math.min(node.getBoundingClientRect().bottom, window.innerHeight)
    }
    node = node.parentElement
  }
  return window.innerHeight
}

export const CountryCodeSelect = ({ value, onChange, className, ariaLabel = 'Country code' }: Props) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [listHeight, setListHeight] = useState(MAX_LIST_HEIGHT)
  const rootRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const selected = getCountry(value)

  useEffect(() => {
    if (!open) {
      setQuery('')
      return
    }
    searchRef.current?.focus()
    // Inside a modal or bottom sheet the list would otherwise be cut off, so fit
    // it to the room available and scroll what's left of it into view.
    if (rootRef.current) {
      const space = clippingBottom(rootRef.current) - rootRef.current.getBoundingClientRect().bottom
      setListHeight(Math.max(MIN_LIST_HEIGHT, Math.min(MAX_LIST_HEIGHT, space - SEARCH_HEIGHT - 12)))
    }
    panelRef.current?.scrollIntoView({ block: 'nearest' })
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return countries
    return countries.filter(
      c =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dialCode.includes(q),
    )
  }, [query])

  const select = (country: Country) => {
    onChange(country.code)
    setOpen(false)
  }

  return (
    <div ref={rootRef} className={twMerge('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-full w-full items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-300 p-3 text-left focus:outline-none focus:ring-2 focus:ring-my-orange"
      >
        <span className="text-lg leading-none">{selected?.flag ?? '🌐'}</span>
        <span className="text-gray-900">{selected?.dialCode ?? '+'}</span>
        <ChevronDownIcon className="size-4 text-gray-500" />
      </button>

      {open && (
        <div
          ref={panelRef}
          className="absolute left-0 z-20 mt-1 w-72 max-w-[85vw] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          <div className="relative border-b border-gray-100 p-2">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search country or code"
              className="w-full rounded-md border border-gray-200 py-2 pl-8 pr-2 text-sm focus:border-my-orange focus:outline-none focus:ring-1 focus:ring-my-orange"
            />
          </div>
          <ul
            role="listbox"
            aria-label={ariaLabel}
            className="overflow-y-auto py-1"
            style={{ maxHeight: listHeight }}
          >
            {results.length === 0 ? (
              <li className="px-3 py-4 text-center text-sm text-gray-500">No matches</li>
            ) : (
              results.map(c => (
                <li key={c.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={c.code === value}
                    onClick={() => select(c)}
                    className={twMerge(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50',
                      c.code === value && 'bg-orange-50 text-my-orange',
                    )}
                  >
                    <span className="text-base leading-none">{c.flag}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="text-gray-500">{c.dialCode}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
